/** Display label: the cleaned-up nickname when present, else the (often messy) catalog name. */
export function displayLabel(item) {
    const nick = item.nickname?.trim();
    return nick && nick.length > 0 ? nick : item.name;
}
export const UNCATEGORIZED = 'Uncategorized';
/**
 * Derive a Browse category from the QBO `fully_qualified_name` hierarchy
 * (colon-delimited, e.g. "Materials:Cable:Cat6" → "Materials"). Falls back to
 * "Uncategorized" for flat/empty FQNs. (A real category column is future work.)
 */
export function deriveCategory(item) {
    const fqn = item.fully_qualified_name?.trim();
    if (fqn && fqn.includes(':')) {
        const top = fqn.split(':')[0]?.trim();
        if (top)
            return top;
    }
    return UNCATEGORIZED;
}
/**
 * Group active items by derived category, alphabetical, with "Uncategorized"
 * last. Inactive items stay out by default because these groups power ticket
 * material pickers; catalog-management surfaces may explicitly opt in.
 */
export function groupByCategory(items, options = {}) {
    const map = new Map();
    for (const item of items) {
        if (!options.includeInactive && !item.is_active)
            continue;
        const cat = deriveCategory(item);
        const bucket = map.get(cat);
        if (bucket)
            bucket.push(item);
        else
            map.set(cat, [item]);
    }
    return [...map.entries()]
        .map(([category, catItems]) => ({ category, items: catItems }))
        .sort((a, b) => {
        if (a.category === UNCATEGORIZED)
            return 1;
        if (b.category === UNCATEGORIZED)
            return -1;
        return a.category.localeCompare(b.category);
    });
}
const tokenize = (s) => s.toLowerCase().split(/\s+/).filter(Boolean);
/**
 * Ranked, instant search over the in-memory catalog. AND semantics across tokens
 * (every token must appear somewhere, matching the backend), OR across columns.
 * Exact / prefix matches on SKU, nickname, and name are boosted so they rank above
 * incidental substring hits buried in a description.
 */
export function searchCatalogIndex(items, rawQuery, limit = 50, options = {}) {
    const q = rawQuery.trim().toLowerCase();
    if (q.length === 0)
        return [];
    const tokens = tokenize(q);
    const scored = [];
    for (const item of items) {
        // This search feeds ticket material pickers. An inactive QBO item may stay
        // resolvable through the raw index, but it must never become addable again.
        if (!options.includeInactive && !item.is_active)
            continue;
        const sku = item.qb_item_id.toLowerCase();
        const name = item.name.toLowerCase();
        const nick = (item.nickname || '').toLowerCase();
        const desc = (item.description || '').toLowerCase();
        const fqn = (item.fully_qualified_name || '').toLowerCase();
        const haystack = `${sku} ${name} ${nick} ${desc} ${fqn}`;
        // Gate: every token must match somewhere (mirrors backend multi-token AND).
        if (!tokens.every((t) => haystack.includes(t)))
            continue;
        let score = 0;
        // Whole-query exact / prefix boosts (most signal).
        if (sku === q)
            score += 1000;
        else if (sku.startsWith(q))
            score += 450;
        if (nick === q || name === q)
            score += 800;
        if (nick && nick.startsWith(q))
            score += 320;
        if (name.startsWith(q))
            score += 220;
        // Substring presence (weaker).
        if (sku.includes(q))
            score += 70;
        if (nick && nick.includes(q))
            score += 45;
        if (name.includes(q))
            score += 30;
        // Tie-breaker: prefer shorter (less noisy) names.
        score -= Math.min(name.length, 120) * 0.1;
        scored.push({ item, score });
    }
    scored.sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name));
    return scored.slice(0, limit).map((s) => s.item);
}
//# sourceMappingURL=catalogIndex.js.map